import React, { useState, useEffect, useCallback } from 'react';
import { salesAPI } from '../services/api';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

function Reports() {
  const [dateRange, setDateRange] = useState({
    start_date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
  });
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const response = await salesAPI.getReport(dateRange);
      setReportData(response.data);
      setCurrentPage(1); // Reset to first page
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleDateChange = () => {
    fetchReport();
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProducts = reportData?.top_products.slice(indexOfFirstItem, indexOfLastItem) || [];
  const totalPages = reportData ? Math.ceil(reportData.top_products.length / itemsPerPage) : 0;

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const exportToCSV = () => {
    const summaryRows = [
      ['รายงานการขาย'],
      [`ช่วงเวลา,${dateRange.start_date},ถึง,${dateRange.end_date}`],
      [`สร้างเมื่อ,${new Date().toLocaleString('th-TH')}`],
      [''],
      ['=== สรุปยอดขาย ==='],
      ['รายการ', 'ยอดเงิน (บาท)'],
      ['รายได้รวม', (reportData.summary.total_revenue || 0).toFixed(2)],
      ['ต้นทุนรวม', (reportData.summary.total_cost || 0).toFixed(2)],
      ['กำไรขั้นต้น', (reportData.summary.gross_profit || 0).toFixed(2)],
      ['ส่วนลดรวม', (reportData.summary.total_discount || 0).toFixed(2)],
      ['กำไรสุทธิ', (reportData.summary.net_profit || 0).toFixed(2)],
      ['อัตรากำไร (%)', (reportData.summary.profit_margin || 0).toFixed(2)],
      ['จำนวนรายการ', reportData.summary.total_transactions || 0],
      ['ยอดเฉลี่ยต่อรายการ', (reportData.summary.average_sale || 0).toFixed(2)],
      [''],
      ['=== รายละเอียดส่วนลด ==='],
      ['ประเภท', 'ยอดเงิน (บาท)'],
      ['ส่วนลดสมาชิก', (reportData.summary.member_discount || 0).toFixed(2)],
      ['ส่วนลดลูกค้าทั่วไป', (reportData.summary.non_member_discount || 0).toFixed(2)],
      ['ส่วนลดจากคะแนน', (reportData.summary.points_discount || 0).toFixed(2)],
      [''],
      ['=== วิธีชำระเงิน ==='],
      ['วิธีชำระ', 'ยอดเงิน (บาท)', 'สัดส่วน (%)'],
      ...reportData.payment_methods.map(method => {
        const methodName = method.payment_method === 'cash' ? 'เงินสด' :
                          method.payment_method === 'transfer' ? 'โอนเงิน' :
                          method.payment_method === 'card' ? 'บัตรเครดิต' : method.payment_method;
        const percentage = reportData.summary.total_revenue > 0
          ? ((method.total / reportData.summary.total_revenue) * 100).toFixed(2)
          : '0.00';
        return [methodName, (method.total || 0).toFixed(2), percentage];
      }),
      [''],
      ['=== สินค้าทั้งหมด (เรียงตามยอดขาย) ==='],
      ['อันดับ', 'ชื่อสินค้า', 'จำนวนขาย', 'รายได้', 'ต้นทุนรวม', 'กำไรรวม', 'ต้นทุน/ชิ้น', 'กำไร/ชิ้น', 'ราคาขายเฉลี่ย', 'มาร์จิ้น (%)'],
      ...reportData.top_products.map((product, index) => [
        index + 1,
        product.product_name,
        product.total_quantity || 0,
        (product.total_revenue || 0).toFixed(2),
        (product.total_cost || 0).toFixed(2),
        (product.total_profit || 0).toFixed(2),
        (product.avg_cost_price || 0).toFixed(2),
        (product.profit_per_unit || 0).toFixed(2),
        (product.avg_selling_price || 0).toFixed(2),
        (product.profit_margin || 0).toFixed(2)
      ])
    ];

    const csvContent = summaryRows
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\r\n');

    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `รายงานการขาย-${dateRange.start_date}-ถึง-${dateRange.end_date}.csv`;
    link.click();
  };

  const exportToHTML = () => {
    const htmlContent = `
<!DOCTYPE html>
<html lang="th">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>รายงานการขาย ${dateRange.start_date} - ${dateRange.end_date}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Sarabun', 'Tahoma', sans-serif;
      background: #fff;
      padding: 30px;
      color: #2d3748;
      font-size: 13px;
      line-height: 1.5;
    }
    .header {
      text-align: center;
      padding: 20px;
      background: #2d3748;
      color: white;
      margin-bottom: 30px;
    }
    .header h1 { font-size: 22px; margin-bottom: 8px; }
    .header p { font-size: 13px; opacity: 0.9; }
    .section { margin-bottom: 30px; }
    .section-title {
      font-size: 16px;
      font-weight: bold;
      color: #2d3748;
      padding: 8px 12px;
      background: #e2e8f0;
      margin-bottom: 15px;
      border-left: 4px solid #4a5568;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 20px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    }
    thead {
      background: #f7fafc;
      border-bottom: 2px solid #cbd5e0;
    }
    th {
      padding: 10px 8px;
      text-align: left;
      font-weight: 600;
      font-size: 11px;
      color: #4a5568;
      text-transform: uppercase;
      border-bottom: 1px solid #e2e8f0;
    }
    td {
      padding: 8px;
      border-bottom: 1px solid #e2e8f0;
      font-size: 12px;
    }
    tbody tr:hover { background: #f7fafc; }
    .text-right { text-align: right; }
    .text-center { text-align: center; }
    .rank {
      display: inline-block;
      width: 28px;
      height: 28px;
      line-height: 26px;
      text-align: center;
      border-radius: 50%;
      font-weight: bold;
      font-size: 12px;
      color: #4a5568;
      border: 2px solid #a0aec0;
      background: transparent;
    }
    .summary-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 15px;
      margin-bottom: 25px;
    }
    .summary-box {
      padding: 15px;
      background: #f7fafc;
      border-left: 3px solid #4a5568;
    }
    .summary-box.revenue { border-left-color: #4299e1; }
    .summary-box.cost { border-left-color: #fc8181; }
    .summary-box.profit { border-left-color: #48bb78; }
    .summary-box.margin { border-left-color: #9f7aea; }
    .summary-label {
      font-size: 11px;
      color: #718096;
      margin-bottom: 5px;
      text-transform: uppercase;
      font-weight: 600;
    }
    .summary-value {
      font-size: 20px;
      font-weight: bold;
      color: #2d3748;
    }
    .footer {
      margin-top: 30px;
      padding-top: 15px;
      border-top: 1px solid #e2e8f0;
      text-align: center;
      font-size: 11px;
      color: #718096;
    }
    @media print {
      body { padding: 10px; }
      @page { margin: 1.5cm; }
      .no-print { display: none; }
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>รายงานการขาย</h1>
    <p>ช่วงเวลา: ${new Date(dateRange.start_date).toLocaleDateString('th-TH')} - ${new Date(dateRange.end_date).toLocaleDateString('th-TH')}</p>
  </div>

  <div class="summary-grid">
    <div class="summary-box revenue">
      <div class="summary-label">รายได้รวม</div>
      <div class="summary-value">฿${(reportData.summary.total_revenue || 0).toLocaleString('th-TH', {minimumFractionDigits: 2})}</div>
    </div>
    <div class="summary-box cost">
      <div class="summary-label">ต้นทุนรวม</div>
      <div class="summary-value">฿${(reportData.summary.total_cost || 0).toLocaleString('th-TH', {minimumFractionDigits: 2})}</div>
    </div>
    <div class="summary-box profit">
      <div class="summary-label">กำไรสุทธิ</div>
      <div class="summary-value">฿${(reportData.summary.net_profit || 0).toLocaleString('th-TH', {minimumFractionDigits: 2})}</div>
    </div>
    <div class="summary-box margin">
      <div class="summary-label">อัตรากำไร</div>
      <div class="summary-value">${(reportData.summary.profit_margin || 0).toFixed(2)}%</div>
    </div>
  </div>

  <div class="section">
    <div class="section-title">สรุปยอดขาย</div>
    <table>
      <tbody>
        <tr><td>จำนวนรายการขาย</td><td class="text-right"><strong>${reportData.summary.total_transactions || 0}</strong> รายการ</td></tr>
        <tr><td>ยอดขายเฉลี่ยต่อรายการ</td><td class="text-right"><strong>฿${(reportData.summary.average_sale || 0).toFixed(2)}</strong></td></tr>
        <tr><td>กำไรขั้นต้น (ก่อนหักส่วนลด)</td><td class="text-right"><strong>฿${(reportData.summary.gross_profit || 0).toFixed(2)}</strong></td></tr>
        <tr><td>ส่วนลดรวม</td><td class="text-right"><strong>฿${(reportData.summary.total_discount || 0).toFixed(2)}</strong></td></tr>
      </tbody>
    </table>
  </div>

  <div class="section">
    <div class="section-title">รายละเอียดส่วนลด</div>
    <table>
      <thead>
        <tr>
          <th>ประเภทส่วนลด</th>
          <th class="text-right">ยอดเงิน (บาท)</th>
        </tr>
      </thead>
      <tbody>
        <tr><td>ส่วนลดสมาชิก</td><td class="text-right">฿${(reportData.summary.member_discount || 0).toFixed(2)}</td></tr>
        <tr><td>ส่วนลดลูกค้าทั่วไป</td><td class="text-right">฿${(reportData.summary.non_member_discount || 0).toFixed(2)}</td></tr>
        <tr><td>ส่วนลดจากคะแนน</td><td class="text-right">฿${(reportData.summary.points_discount || 0).toFixed(2)}</td></tr>
      </tbody>
    </table>
  </div>

  <div class="section">
    <div class="section-title">วิธีชำระเงิน</div>
    <table>
      <thead>
        <tr>
          <th>วิธีชำระเงิน</th>
          <th class="text-right">ยอดเงิน (บาท)</th>
          <th class="text-right">สัดส่วน (%)</th>
        </tr>
      </thead>
      <tbody>
        ${reportData.payment_methods.map(method => {
          const methodName = method.payment_method === 'cash' ? 'เงินสด' :
                            method.payment_method === 'transfer' ? 'โอนเงิน' :
                            method.payment_method === 'card' ? 'บัตรเครดิต' : method.payment_method;
          const percentage = reportData.summary.total_revenue > 0
            ? ((method.total / reportData.summary.total_revenue) * 100).toFixed(2)
            : '0.00';
          return `<tr>
            <td>${methodName}</td>
            <td class="text-right">฿${(method.total || 0).toFixed(2)}</td>
            <td class="text-right">${percentage}%</td>
          </tr>`;
        }).join('')}
      </tbody>
    </table>
  </div>

  <div class="section">
    <div class="section-title">สินค้าทั้งหมด (เรียงตามยอดขายมากสุด)</div>
    <table>
      <thead>
        <tr>
          <th class="text-center" style="width: 50px;">อันดับ</th>
          <th>ชื่อสินค้า</th>
          <th class="text-center" style="width: 80px;">จำนวนขาย</th>
          <th class="text-right" style="width: 100px;">รายได้</th>
          <th class="text-right" style="width: 100px;">ต้นทุนรวม</th>
          <th class="text-right" style="width: 100px;">กำไรรวม</th>
          <th class="text-right" style="width: 90px;">ต้นทุน/ชิ้น</th>
          <th class="text-right" style="width: 90px;">กำไร/ชิ้น</th>
          <th class="text-right" style="width: 70px;">มาร์จิ้น</th>
        </tr>
      </thead>
      <tbody>
        ${reportData.top_products.map((product, index) => `
          <tr>
            <td class="text-center">
              <span class="rank">
                ${index + 1}
              </span>
            </td>
            <td><strong>${product.product_name}</strong></td>
            <td class="text-center">${product.total_quantity || 0}</td>
            <td class="text-right">฿${(product.total_revenue || 0).toFixed(2)}</td>
            <td class="text-right">฿${(product.total_cost || 0).toFixed(2)}</td>
            <td class="text-right">฿${(product.total_profit || 0).toFixed(2)}</td>
            <td class="text-right">฿${(product.avg_cost_price || 0).toFixed(2)}</td>
            <td class="text-right">฿${(product.profit_per_unit || 0).toFixed(2)}</td>
            <td class="text-right">${(product.profit_margin || 0).toFixed(2)}%</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
  </div>

  <div class="footer">
    <p>สร้างเมื่อ: ${new Date().toLocaleString('th-TH')}</p>
    <p>ระบบจัดการร้านค้า POS</p>
  </div>

  <script>
    window.onload = () => { setTimeout(() => window.print(), 500); };
  </script>
</body>
</html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  if (loading || !reportData) {
    return <div className="p-6">กำลังโหลดรายงาน...</div>;
  }

  return (
    <div className="p-6">
      <h2 className="text-3xl font-bold mb-6">รายงานการขาย</h2>

      {/* Date Range Filter */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex gap-4 items-end justify-between">
          <div className="flex gap-4 items-end">
            <div>
              <label className="block text-sm font-medium mb-1">วันที่เริ่มต้น</label>
              <input
                type="date"
                value={dateRange.start_date}
                onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
                className="p-2 border rounded focus:ring-2 focus:ring-gray-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">วันที่สิ้นสุด</label>
              <input
                type="date"
                value={dateRange.end_date}
                onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
                className="p-2 border rounded focus:ring-2 focus:ring-gray-500"
              />
            </div>
            <button
              onClick={handleDateChange}
              className="bg-gray-700 hover:bg-gray-800 text-white font-bold py-2 px-4 rounded"
            >
              อัปเดตรายงาน
            </button>
          </div>
          <div className="flex gap-2">
            <button
              onClick={exportToCSV}
              className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export CSV
            </button>
            <button
              onClick={exportToHTML}
              className="bg-gray-700 hover:bg-gray-800 text-white font-bold py-2 px-4 rounded flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Export PDF
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-blue-600">
          <div className="text-gray-500 text-sm font-medium">รายได้รวม</div>
          <div className="text-3xl font-bold text-blue-600 mt-2">
            ฿{(reportData.summary.total_revenue || 0).toLocaleString('th-TH', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </div>
          <div className="text-sm text-gray-600 mt-1">
            {reportData.summary.total_transactions || 0} รายการ
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-red-600">
          <div className="text-gray-500 text-sm font-medium">ต้นทุนรวม</div>
          <div className="text-3xl font-bold text-red-600 mt-2">
            ฿{(reportData.summary.total_cost || 0).toLocaleString('th-TH', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </div>
          <div className="text-sm text-gray-600 mt-1">
            ทุนสินค้าที่ขาย
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-green-600">
          <div className="text-gray-500 text-sm font-medium">กำไรสุทธิ</div>
          <div className="text-3xl font-bold text-green-600 mt-2">
            ฿{(reportData.summary.net_profit || 0).toLocaleString('th-TH', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </div>
          <div className="text-sm text-gray-600 mt-1">
            หลังหักส่วนลด
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-purple-600">
          <div className="text-gray-500 text-sm font-medium">อัตรากำไร</div>
          <div className="text-3xl font-bold text-purple-600 mt-2">
            {(reportData.summary.profit_margin || 0).toFixed(2)}%
          </div>
          <div className="text-sm text-gray-600 mt-1">
            มาร์จิ้นกำไรสุทธิ
          </div>
        </div>
      </div>

      {/* Detailed Summary Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Sales Details */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-bold mb-4 border-b pb-2">สรุปข้อมูลการขาย</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
              <span className="text-gray-600">ยอดขายเฉลี่ย</span>
              <span className="font-bold text-gray-800">฿{(reportData.summary.average_sale || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
              <span className="text-gray-600">กำไรขั้นต้น (ก่อนหักส่วนลด)</span>
              <span className="font-bold text-gray-800">฿{(reportData.summary.gross_profit || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
              <span className="text-gray-600">ส่วนลดรวม</span>
              <span className="font-bold text-orange-600">฿{(reportData.summary.total_discount || 0).toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Discount Breakdown */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-bold mb-4 border-b pb-2">รายละเอียดส่วนลด</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
              <span className="text-gray-600">ส่วนลดสมาชิก</span>
              <span className="font-bold text-gray-800">฿{(reportData.summary.member_discount || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
              <span className="text-gray-600">ส่วนลดลูกค้าทั่วไป</span>
              <span className="font-bold text-gray-800">฿{(reportData.summary.non_member_discount || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
              <span className="text-gray-600">ส่วนลดจากคะแนน</span>
              <span className="font-bold text-purple-600">฿{(reportData.summary.points_discount || 0).toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Methods */}
      <div className="bg-white p-6 rounded-lg shadow mb-8">
        <h3 className="text-lg font-bold mb-4 border-b pb-2">วิธีชำระเงิน</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {reportData.payment_methods.map((method, index) => {
            const methodName = method.payment_method === 'cash' ? 'เงินสด' :
                              method.payment_method === 'transfer' ? 'โอนเงิน' :
                              method.payment_method === 'card' ? 'บัตรเครดิต/เดบิต' : method.payment_method;
            return (
              <div key={index} className="flex justify-between items-center p-4 bg-gray-50 rounded border">
                <span className="text-gray-600 font-medium">{methodName}</span>
                <span className="font-bold text-gray-800">฿{(method.total || 0).toFixed(2)}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Daily Sales Chart */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-bold mb-4">แนวโน้มยอดขายรายวัน</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={reportData.daily_sales}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="revenue" stroke="#3b82f6" name="รายได้ (บาท)" strokeWidth={2} />
              <Line type="monotone" dataKey="transactions" stroke="#10b981" name="รายการ" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Top Products Bar Chart */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-bold mb-4">10 สินค้าขายดี</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={reportData.top_products.slice(0, 10)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="product_name" angle={-45} textAnchor="end" height={100} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="total_quantity" fill="#3b82f6" name="จำนวนที่ขาย" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* All Products Table with Pagination */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b bg-gray-50 flex justify-between items-center">
          <div>
            <h3 className="text-lg font-bold">สินค้าทั้งหมด (เรียงตามยอดขายมากสุด)</h3>
            <p className="text-sm text-gray-600 mt-1">
              แสดง {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, reportData.top_products.length)} จากทั้งหมด {reportData.top_products.length} รายการ
            </p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">อันดับ</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">สินค้า</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">จำนวนขาย</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">รายได้</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">ต้นทุนรวม</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">กำไรรวม</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">ต้นทุน/ชิ้น</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">กำไร/ชิ้น</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">มาร์จิ้น %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {currentProducts.map((product, index) => {
                const actualIndex = indexOfFirstItem + index;
                return (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full border-2 border-gray-400 text-gray-700 font-bold text-sm">
                        {actualIndex + 1}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900">{product.product_name}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-center font-semibold">{product.total_quantity || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-right font-bold text-blue-600">฿{(product.total_revenue || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-right font-bold text-red-600">฿{(product.total_cost || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-right font-bold text-green-600">฿{(product.total_profit || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-gray-700">฿{(product.avg_cost_price || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-green-700 font-semibold">฿{(product.profit_per_unit || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-right font-bold text-purple-600">{(product.profit_margin || 0).toFixed(2)}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-between">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded ${
                currentPage === 1
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-600 text-white hover:bg-gray-700'
              }`}
            >
              ก่อนหน้า
            </button>

            <div className="flex gap-2">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => {
                // Show first page, last page, current page, and pages around current
                if (
                  number === 1 ||
                  number === totalPages ||
                  (number >= currentPage - 2 && number <= currentPage + 2)
                ) {
                  return (
                    <button
                      key={number}
                      onClick={() => paginate(number)}
                      className={`px-3 py-1 rounded ${
                        currentPage === number
                          ? 'bg-gray-700 text-white font-bold'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {number}
                    </button>
                  );
                } else if (
                  number === currentPage - 3 ||
                  number === currentPage + 3
                ) {
                  return <span key={number} className="px-2">...</span>;
                }
                return null;
              })}
            </div>

            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded ${
                currentPage === totalPages
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-600 text-white hover:bg-gray-700'
              }`}
            >
              ถัดไป
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Reports;
